// Admin console: one knowledge file, raw (GET /knowledge/files/:file).
// Regions (design review ui.region_gate raw_view page):
//   header   plain spacing    back to the list, the title, the file name, Ganti isi, Hapus
//   facts    divided section  Kategori, Tahap, Bagian or butir, Ukuran, Diperbarui
//   raw      card             the text as stored, scrolling inside its own box
// System files cannot be deleted: Hapus stays disabled with the reason beside it.
import { useState } from "react";
import * as API from "../api";
import { formatCount, formatDateTime } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL } from "../lib/labels";
import { navigate, useLocation } from "../router";
import { Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { DeleteFileDialog, fileHref, tahapText } from "./Knowledge";
import { bytesOf, formatSize, isSystemFile, lineCount, SYSTEM_FILES } from "./knowledge-lib";

export function KnowledgeFileScreen({ file }: { file: string }) {
  const system = isSystemFile(file);
  const meta = SYSTEM_FILES[file];
  const loc = useLocation();
  const raw = useAsync(() => API.getKnowledgeFile(file), [file]);
  const list = useAsync(() => API.getAdminKnowledge(), [file]);
  const [deleting, setDeleting] = useState(false);
  const entry = list.data?.files.find((f) => f.file === file) ?? null;
  const sys = list.data?.system?.find((s) => s.file === file) ?? null;
  const title = meta?.label ?? entry?.title ?? file;
  useTitle(title);
  const saved = loc.query.get("disimpan") === "1";

  const facts: [string, string][] = system
    ? [
        ["Jenis", "Berkas sistem"],
        ["Isi", sys ? `${formatCount(sys.itemCount)} ${meta!.unit}` : "Tidak diketahui"],
        ["Ukuran", raw.data ? formatSize(bytesOf(raw.data.content)) : "Memuat"],
        ["Diperbarui", sys ? formatDateTime(sys.updatedAt) : "Tidak diketahui"],
      ]
    : [
        ["Kategori", entry ? (KNOWLEDGE_CATEGORY_LABEL[entry.category] ?? entry.category) : "Tidak dimuat"],
        ["Tahap", entry ? tahapText(entry.tahap) : "Tidak dimuat"],
        ["Bagian", entry ? formatCount(entry.sectionCount) : "Tidak dimuat"],
        ["Diperbarui", entry?.updatedAt ? formatDateTime(entry.updatedAt) : "Tidak diketahui"],
      ];
  const failed = list.data?.errors.find((e) => e.file === file) ?? null;

  return (
    <>
      <PageHeader
        back={{ href: "/admin/pengetahuan", label: "Basis Pengetahuan" }}
        title={title}
        lead={<span className="mono">{file}</span>}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={system || !raw.data}
              aria-describedby={system ? "kb-system-note" : undefined}
              onClick={() => setDeleting(true)}
            >
              <Glyph name="trash" />
              <span>Hapus</span>
            </button>
            <a className="btn" href={fileHref(file, "ganti")}>
              <Glyph name="edit2" />
              <span>Ganti isi</span>
            </a>
          </>
        }
      />

      {saved ? (
        <Notice tone="success" title="Berkas tersimpan dan dimuat ulang">
          <p>Pluto memakai isi di bawah mulai pertanyaan dan draf berikutnya.</p>
        </Notice>
      ) : null}
      {failed ? (
        <Notice tone="danger" title="Berkas ini gagal dimuat">
          <p>{failed.message}</p>
        </Notice>
      ) : null}

      <section className="region divided" aria-label="Data berkas">
        <dl className="facts">
          {facts.map(([k, v]) => (
            <div key={k} className="fact">
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        {system ? (
          <p className="quiet-line" id="kb-system-note">
            <Glyph name="lock" />
            <span>Berkas sistem tidak dapat dihapus. Isinya dapat diganti setelah lolos pemeriksaan format.</span>
          </p>
        ) : null}
      </section>

      <section className="region" aria-labelledby="kb-raw-title">
        <div className="section-head">
          <div className="section-head-text">
            <h2 id="kb-raw-title" className="section-title">
              Isi berkas
            </h2>
            <p className="section-lead">
              {raw.data ? `${formatCount(lineCount(raw.data.content))} baris, ${formatSize(bytesOf(raw.data.content))}, persis seperti tersimpan di server.` : "Teks mentah berkas."}
            </p>
          </div>
        </div>
        {raw.loading && !raw.data ? (
          <SkeletonRows count={8} label="Memuat isi berkas" />
        ) : raw.error ? (
          <Notice
            tone="danger"
            title={raw.error instanceof API.ApiError && raw.error.status === 404 ? "Berkas tidak ditemukan" : "Isi berkas belum dapat dimuat"}
            action={
              <a className="btn btn-secondary" href="/admin/pengetahuan">
                Kembali ke Basis Pengetahuan
              </a>
            }
          >
            <p>{API.errorMessage(raw.error)}</p>
          </Notice>
        ) : raw.data ? (
          <pre className="raw-text" tabIndex={0} aria-label={`Isi ${file}`}>
            {raw.data.content}
          </pre>
        ) : null}
      </section>

      {deleting && !system ? (
        <DeleteFileDialog
          file={file}
          title={title}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            navigate("/admin/pengetahuan");
          }}
        />
      ) : null}
    </>
  );
}
